import React from 'react'
import {
  Container,
  Indicator,
  PercentBaseIndicator,
  PercentStatusIndicator,
  PercentStatusLabel,
  Progress,
  ProgressBarGradient,
  ProgressIndicator,
  ReferenceLink,
  ReferenceValue,
  Title
} from './styles'
import { message } from '~/services'
import { REFERENCE_PRS_URL } from '~/constants'
import { Inline, Stack } from '~/styles'
import { IBreastCancerPRS } from '~/interfaces'
import { Tooltip } from '~/components/shared/tooltip'

const POPULATION_RISK_LIMIT = 98.9

const PercentStatus = ({ variant }: { variant: 'increased' | 'population' }) => (
  <Inline spacing="4px">
    <PercentStatusIndicator variation={variant} />
    <PercentStatusLabel>
      {variant === 'increased' && message('analyticalModels.breast_cancer_prs_increased_risk_range')}
      {variant === 'population' && message('analyticalModels.breast_cancer_prs_population_risk_range')}
    </PercentStatusLabel>
  </Inline>
)

const Header = (): JSX.Element => (
  <>
    <Inline marginBottom="4px">
      <Title>{message('analyticalModels.breast_cancer_prs_title')}</Title>
    </Inline>
    <Stack spacing="4px" alignItems="flex-start" marginBottom="0.875rem">
      <PercentStatus variant="population" />
      <PercentStatus variant="increased" />
    </Stack>
  </>
)

const ProgressStep = ({ percent }: { percent: number }): JSX.Element => (
  <PercentBaseIndicator percent={percent}>
    <Progress>{percent}%</Progress>
  </PercentBaseIndicator>
)

const ProgressBar = ({ progress }: { progress: number }): JSX.Element => {
  return (
    <Stack marginBottom="0.5rem">
      <Stack width="100%" marginBottom="1rem" spacing="4px" alignItems="flex-start">
        <ProgressIndicator progress={progress}>
          <Tooltip
            position="top"
            cursor="pointer"
            opacity={0.8}
            description={
              progress > POPULATION_RISK_LIMIT
                ? message('analyticalModels.breast_cancer_prs_increased_risk')
                : message('analyticalModels.breast_cancer_prs_population_risk')
            }
          >
            <Stack position="relative" spacing="0">
              <Progress>{progress}%</Progress>
              <Indicator color={progress > POPULATION_RISK_LIMIT ? '#b82116' : '#146630'} />
            </Stack>
          </Tooltip>
        </ProgressIndicator>

        <ProgressBarGradient />
      </Stack>

      <ProgressStep percent={0} />
      <ProgressStep percent={20} />
      <ProgressStep percent={40} />
      <ProgressStep percent={60} />
      <ProgressStep percent={80} />
      <ProgressStep percent={100} />
    </Stack>
  )
}

const Reference = (): JSX.Element => (
  <ReferenceValue>
    {message('analyticalModels.reference')}:{' '}
    <ReferenceLink href={REFERENCE_PRS_URL} target="_blank">
      {REFERENCE_PRS_URL}
    </ReferenceLink>
  </ReferenceValue>
)

export interface BreastCancerPrsProps extends React.CSSProperties {
  prs: IBreastCancerPRS
}

export const BreastCancerPrs = ({ prs, ...rest }: BreastCancerPrsProps): JSX.Element => {
  return (
    <Container style={{ ...rest }}>
      <Header />
      <ProgressBar progress={Math.trunc(prs.quantile * 100)} />
      <Reference />
    </Container>
  )
}
